import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, RefreshCw, LogOut, Users, UserCheck, UserX, Lock, ArrowLeft } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { THEMES } from "@/themes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const theme = THEMES[1];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("ss-admin") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${t}` };
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/rsvps`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setRsvps(r.data.rsvps);
      setStats(s.data);
    } catch {
      sessionStorage.removeItem("ss-admin");
      setToken("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("ss-admin", data.token);
      setToken(data.token);
    } catch {
      setError("Senha incorreta. Tente novamente.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("ss-admin");
    setToken("");
    setPassword("");
    setRsvps([]);
    setStats(null);
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/rsvps/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "confirmacoes-sports-sweat.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="theme-marble min-h-screen px-6 py-10"
      style={{ ...theme.vars, background: "var(--bg)", color: "var(--ink)" }}
      data-testid="admin-page"
    >
      <div className="grain" />
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center justify-between">
          <a
            data-testid="back-to-invite"
            href="/"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase opacity-60 hover:opacity-100 transition-opacity"
            style={{ color: "var(--muted)" }}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Convite
          </a>
          <LogoMark className="w-9 h-9 opacity-80" />
        </div>

        {!token ? (
          <motion.form
            onSubmit={login}
            data-testid="admin-login-form"
            className="lux-card max-w-sm mx-auto mt-24 px-8 py-12 text-center"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="inline-flex items-center justify-center w-12 h-12 rounded-full"
              style={{ border: "1px solid var(--gold)" }}
            >
              <Lock className="w-5 h-5" style={{ color: "var(--gold)" }} />
            </span>
            <h1 className="font-display gold-text text-2xl mt-5">Painel do organizador</h1>
            <p className="text-xs tracking-[0.2em] uppercase mt-2" style={{ color: "var(--muted)" }}>
              Confirmações de presença
            </p>
            <input
              data-testid="admin-password-input"
              type="password"
              className="lux-input mt-8 text-center"
              placeholder="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400 mt-4">
                {error}
              </p>
            )}
            <button data-testid="admin-login-button" type="submit" className="lux-btn w-full mt-8">
              Entrar
            </button>
          </motion.form>
        ) : (
          <motion.div data-testid="admin-dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-wrap items-center justify-between gap-4 mt-12">
              <h1 className="font-display gold-text text-3xl">Confirmações</h1>
              <div className="flex gap-3">
                <button data-testid="refresh-button" onClick={() => load(token)} className="lux-btn !px-5 !py-3">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
                </button>
                <button data-testid="export-csv-button" onClick={exportCsv} className="lux-btn !px-5 !py-3">
                  <Download className="w-4 h-4" /> Exportar CSV
                </button>
                <button data-testid="logout-button" onClick={logout} className="lux-btn !px-5 !py-3">
                  <LogOut className="w-4 h-4" /> Sair
                </button>
              </div>
            </div>

            {stats && (
              <div className="grid grid-cols-3 gap-4 mt-10">
                <div className="lux-card px-4 py-6 text-center" data-testid="stat-confirmed">
                  <UserCheck className="w-5 h-5 mx-auto" style={{ color: "var(--gold)" }} />
                  <p className="font-display gold-text text-3xl mt-2">{stats.confirmados}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase mt-1" style={{ color: "var(--muted)" }}>
                    Confirmados
                  </p>
                </div>
                <div className="lux-card px-4 py-6 text-center" data-testid="stat-declined">
                  <UserX className="w-5 h-5 mx-auto" style={{ color: "var(--gold)" }} />
                  <p className="font-display gold-text text-3xl mt-2">{stats.ausentes}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase mt-1" style={{ color: "var(--muted)" }}>
                    Não vão
                  </p>
                </div>
                <div className="lux-card px-4 py-6 text-center" data-testid="stat-total">
                  <Users className="w-5 h-5 mx-auto" style={{ color: "var(--gold)" }} />
                  <p className="font-display gold-text text-3xl mt-2">{stats.total}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase mt-1" style={{ color: "var(--muted)" }}>
                    Respostas
                  </p>
                </div>
              </div>
            )}

            <div className="lux-card mt-8 overflow-x-auto">
              <table className="w-full text-sm" data-testid="rsvp-table">
                <thead>
                  <tr className="text-left text-[11px] tracking-[0.25em] uppercase" style={{ color: "var(--muted)" }}>
                    <th className="px-5 py-4 font-medium">Nome</th>
                    <th className="px-5 py-4 font-medium">Instagram</th>
                    <th className="px-5 py-4 font-medium">Presença</th>
                    <th className="px-5 py-4 font-medium">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-5 py-10 text-center" style={{ color: "var(--muted)" }}>
                        Nenhuma confirmação ainda.
                      </td>
                    </tr>
                  )}
                  {rsvps.map((r) => (
                    <tr key={r.id} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`rsvp-row-${r.id}`}>
                      <td className="px-5 py-4 font-medium">{r.nome}</td>
                      <td className="px-5 py-4" style={{ color: "var(--muted)" }}>
                        {r.instagram ? `@${r.instagram}` : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className="text-[11px] tracking-[0.15em] uppercase px-3 py-1"
                          style={
                            r.presenca === "sim"
                              ? { border: "1px solid var(--gold)", color: "var(--gold)" }
                              : { border: "1px solid var(--line)", color: "var(--muted)" }
                          }
                        >
                          {r.presenca === "sim" ? "Vai" : "Não vai"}
                        </span>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" style={{ color: "var(--muted)" }}>
                        {new Date(r.created_at).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
